import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Eye, EyeOff, Hash, Lock, Mail, Minus, Plus } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { AUTH_STORES_OAUTH_GOOGLE } from '../../../../shared/constants/endpoints';
import './LoginForm.css';

const LoginForm = ({
  storeLogin,
  onStoreLoginFieldChange,
  onSuccess,
  onToast,
  request,
  onGoogleAuthSuccess,
  isLoading,
  onOpenForgotPassword,
  showRegister,
  onScrollToRegister
}) => {
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false, storeCode: false });
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState('');
  const [googleAccountOptions, setGoogleAccountOptions] = useState([]);
  const [googleAccountSearch, setGoogleAccountSearch] = useState('');
  const [isGoogleAccountMenuOpen, setIsGoogleAccountMenuOpen] = useState(false);
  const [isResolvingGoogleAccount, setIsResolvingGoogleAccount] = useState(false);
  const googleButtonWrapperRef = useRef(null);
  const [googleButtonWidth, setGoogleButtonWidth] = useState(0);

  const completeGoogleLogin = useCallback(async ({ credential, selectedAccountType = '', selectedStoreUuid = '' }) => {
    if (!credential) {
      onToast?.({ message: 'Google did not return a valid credential', variant: 'error' });
      return;
    }

    try {
      const response = await request(AUTH_STORES_OAUTH_GOOGLE, {
        method: 'POST',
        body: JSON.stringify({
          credential,
          loginContext: 'STORE_PORTAL',
          selectedAccountType,
          selectedStoreUuid
        }),
        requestName: 'google-oauth'
      });

      if (response.success) {
        const authData = response.data;
        setPendingGoogleCredential('');
        setGoogleAccountOptions([]);
        setGoogleAccountSearch('');
        setIsGoogleAccountMenuOpen(false);
        onGoogleAuthSuccess?.(authData);
        onToast?.({ message: 'Login successful!', variant: 'success' });
        return;
      }

      onToast?.({ message: response.message || 'Google login failed', variant: 'error' });
    } catch (error) {
      const selectionPayload = error?.data?.data;
      if (error?.status === 409 && selectionPayload?.requiresAccountSelection) {
        setPendingGoogleCredential(credential);
        setGoogleAccountOptions(Array.isArray(selectionPayload.options) ? selectionPayload.options : []);
        setGoogleAccountSearch('');
        setIsGoogleAccountMenuOpen(true);
        onToast?.({ message: error?.data?.message || 'Select an account to continue', variant: 'info' });
        return;
      }

      const errorMessage = error?.data?.message || error?.message || 'Google login failed';
      onToast?.({ message: errorMessage, variant: 'error' });
    }
  }, [onGoogleAuthSuccess, onToast, request]);

  const handleGoogleSignInSuccess = useCallback(async (credentialResponse) => {
    await completeGoogleLogin({ credential: credentialResponse?.credential || '' });
  }, [completeGoogleLogin]);

  const handleGoogleSignInError = useCallback(() => {
    onToast?.({ message: 'Google login failed', variant: 'error' });
  }, [onToast]);

  const handleGoogleAccountSelection = useCallback(async (option) => {
    if (!pendingGoogleCredential) {
      onToast?.({ message: 'Google session expired. Please sign in again.', variant: 'error' });
      setGoogleAccountOptions([]);
      return;
    }

    setIsResolvingGoogleAccount(true);
    try {
      await completeGoogleLogin({
        credential: pendingGoogleCredential,
        selectedAccountType: option?.accountType || '',
        selectedStoreUuid: option?.storeUuid || ''
      });
    } finally {
      setIsResolvingGoogleAccount(false);
    }
  }, [completeGoogleLogin, onToast, pendingGoogleCredential]);

  const filteredGoogleAccountOptions = useMemo(() => googleAccountOptions.filter((option) => {
    const searchValue = googleAccountSearch.trim().toLowerCase();
    if (!searchValue) {
      return true;
    }

    const fields = [
      option.label,
      option.subtitle,
      option.storeCode,
      option.storeName,
      option.city,
      option.area,
      option.state,
      option.pincode,
      option.accountType
    ];

    return fields.some((value) => String(value || '').toLowerCase().includes(searchValue));
  }), [googleAccountOptions, googleAccountSearch]);

  const filteredStoreAccountOptions = filteredGoogleAccountOptions.filter((option) => option.accountType === 'STORE');
  const filteredUserAccountOptions = filteredGoogleAccountOptions.filter((option) => option.accountType === 'USER');
  const isCompactPicker = googleAccountOptions.length > 2;

  const loginErrors = useMemo(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = String(storeLogin.email || '').trim();
    return {
      email: !email ? 'Email is required' : !emailPattern.test(email) ? 'Enter a valid email address' : '',
      password: !storeLogin.password ? 'Password is required' : '',
      storeCode: !storeLogin.storeCode ? 'Store code is required' : ''
    };
  }, [storeLogin.email, storeLogin.password, storeLogin.storeCode]);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  useEffect(() => {
    const wrapper = googleButtonWrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const updateGoogleButtonWidth = () => {
      const nextWidth = Math.floor(wrapper.getBoundingClientRect().width || 0);
      setGoogleButtonWidth(nextWidth > 0 ? Math.min(nextWidth, 400) : 0);
    };

    updateGoogleButtonWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateGoogleButtonWidth);
      return () => window.removeEventListener('resize', updateGoogleButtonWidth);
    }

    const resizeObserver = new ResizeObserver(updateGoogleButtonWidth);
    resizeObserver.observe(wrapper);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className="card login-card login-form-shell">
      <div className="portal-header login-portal-header">
        <h1 className="portal-title">Sign in to Zopeo</h1>
        <p className="portal-subtitle">Access your dashboard to manage your enterprise.</p>
      </div>

      <div className="google-auth-shell">
        <div ref={googleButtonWrapperRef} className="google-btn-wrapper">
          <div className="google-btn-center">
            {googleButtonWidth > 0 && (
              <GoogleLogin
                onSuccess={handleGoogleSignInSuccess}
                onError={handleGoogleSignInError}
                theme="outline"
                size="large"
                text="continue_with"
                shape="pill"
                logo_alignment="center"
                width={String(googleButtonWidth)}
                useOneTap
              />
            )}
          </div>
        </div>

        {googleAccountOptions.length > 0 && (
          <div className="google-account-picker">
            <button
              type="button"
              className="google-account-trigger"
              onClick={() => setIsGoogleAccountMenuOpen((prev) => !prev)}
              disabled={isResolvingGoogleAccount}
              aria-expanded={isGoogleAccountMenuOpen}
            >
              <span className="google-account-trigger-copy">
                <span className="google-account-picker-eyebrow">Linked accounts</span>
                <span className="google-account-trigger-title">Choose account</span>
                <span className="google-account-trigger-meta">
                  {googleAccountOptions.length} account{googleAccountOptions.length > 1 ? 's' : ''} found for this Google email
                </span>
              </span>
              <span className="google-account-trigger-icon">{isGoogleAccountMenuOpen ? <Minus size={16} /> : <Plus size={16} />}</span>
            </button>

            {isGoogleAccountMenuOpen && (
              <div className="google-account-dropdown">
                <div className="google-account-picker-header">
                  <div className="google-account-picker-copy">
                    <h5>Select where to continue</h5>
                    <p>Search and choose the exact store or customer account connected to this Google email.</p>
                  </div>
                  <span className="google-account-picker-count">{filteredGoogleAccountOptions.length} visible</span>
                </div>

                <div className="google-account-search-shell">
                  <span className="google-account-search-icon">/</span>
                  <input
                    className="input google-account-search-input"
                    type="text"
                    value={googleAccountSearch}
                    onChange={(event) => setGoogleAccountSearch(event.target.value)}
                    placeholder="Search by store name, code, city, area or pincode"
                  />
                </div>

                <div className={`google-account-dropdown-list${isCompactPicker ? ' compact' : ''}`}>
                  {filteredStoreAccountOptions.length > 0 && (
                    <div className="google-account-group">
                      <div className="google-account-group-header">
                        <span className="google-account-group-title">Store accounts</span>
                        <span className="google-account-group-count">{filteredStoreAccountOptions.length}</span>
                      </div>
                      <div className="google-account-picker-list">
                        {filteredStoreAccountOptions.map((option) => {
                          const optionKey = `${option.accountType}-${option.storeUuid || option.label}`;
                          return (
                            <button
                              key={optionKey}
                              type="button"
                              className={`google-account-option${isCompactPicker ? ' compact' : ''}`}
                              onClick={() => handleGoogleAccountSelection(option)}
                              disabled={isResolvingGoogleAccount}
                            >
                              {isCompactPicker ? (
                                <>
                                  <span className="google-account-compact-left">
                                    <span className="google-account-option-title">{option.label}</span>
                                    {option.subtitle && <span className="google-account-compact-meta">{option.subtitle}</span>}
                                  </span>
                                  <ChevronRight size={14} className="google-account-compact-arrow" aria-hidden="true" />
                                </>
                              ) : (
                                <>
                                  <span className="google-account-option-top">
                                    <span className="google-account-option-title">{option.label}</span>
                                  </span>
                                  <span className="google-account-option-meta">{option.subtitle}</span>
                                  <span className="google-account-option-action">
                                    {isResolvingGoogleAccount ? 'Connecting...' : 'Continue to store'}
                                  </span>
                                </>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {filteredUserAccountOptions.length > 0 && (
                    <div className="google-account-group">
                      <div className="google-account-group-header">
                        <span className="google-account-group-title">Customer account</span>
                        <span className="google-account-group-count">{filteredUserAccountOptions.length}</span>
                      </div>
                      <div className="google-account-picker-list">
                        {filteredUserAccountOptions.map((option) => {
                          const optionKey = `${option.accountType}-${option.storeUuid || option.label}`;
                          return (
                            <button
                              key={optionKey}
                              type="button"
                              className={`google-account-option${isCompactPicker ? ' compact' : ''}`}
                              onClick={() => handleGoogleAccountSelection(option)}
                              disabled={isResolvingGoogleAccount}
                            >
                              {isCompactPicker ? (
                                <>
                                  <span className="google-account-compact-left">
                                    <span className="google-account-badge user">Customer</span>
                                    <span className="google-account-option-title">{option.label}</span>
                                    {option.subtitle && <span className="google-account-compact-meta">{option.subtitle}</span>}
                                  </span>
                                  <ChevronRight size={14} className="google-account-compact-arrow" aria-hidden="true" />
                                </>
                              ) : (
                                <>
                                  <span className="google-account-option-top">
                                    <span className="google-account-option-title">{option.label}</span>
                                    <span className="google-account-badge user">Customer</span>
                                  </span>
                                  <span className="google-account-option-meta">{option.subtitle}</span>
                                  <span className="google-account-option-action">
                                    {isResolvingGoogleAccount ? 'Connecting...' : 'Continue to user'}
                                  </span>
                                </>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {filteredGoogleAccountOptions.length === 0 && (
                    <div className="google-account-empty">
                      No matching accounts found for this search.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="login-divider">
        <span>or sign in with email</span>
      </div>

      <div className="form-grid">
        <div className="input-group">
          <label className="input-label">Email</label>
          <div className="auth-input-shell">
            <span className="auth-input-icon" aria-hidden="true"><Mail size={16} /></span>
            <input
              className={`input auth-input${touched.email && loginErrors.email ? ' error' : ''}`}
              placeholder="you@company.com"
              value={storeLogin.email}
              maxLength={50}
              onChange={(event) => onStoreLoginFieldChange('email', event.target.value)}
              onBlur={() => handleBlur('email')}
            />
          </div>
          {touched.email && loginErrors.email && <p className="field-error" role="alert">{loginErrors.email}</p>}
        </div>
        <div className="input-group">
          <div className="login-password-row">
            <label className="input-label">Password</label>
            <button type="button" className="forgot-password-link" onClick={onOpenForgotPassword}>
              Forgot password?
            </button>
          </div>
          <div className="auth-input-shell auth-input-shell--with-action">
            <span className="auth-input-icon" aria-hidden="true"><Lock size={16} /></span>
            <input
              className={`input auth-input${touched.password && loginErrors.password ? ' error' : ''}`}
              type={showLoginPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={storeLogin.password || ''}
              maxLength={128}
              onChange={(event) => onStoreLoginFieldChange('password', event.target.value)}
              onBlur={() => handleBlur('password')}
            />
            <button
              type="button"
              className="copy-btn auth-input-action"
              onClick={() => setShowLoginPassword((prev) => !prev)}
            >
              {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {touched.password && loginErrors.password && <p className="field-error" role="alert">{loginErrors.password}</p>}
        </div>
        <div className="input-group">
          <label className="input-label">Store Code</label>
          <div className="auth-input-shell">
            <span className="auth-input-icon" aria-hidden="true"><Hash size={16} /></span>
            <input
              className={`input auth-input${touched.storeCode && loginErrors.storeCode ? ' error' : ''}`}
              placeholder="e.g. ZOPEO-PHOENIX-01"
              value={storeLogin.storeCode}
              maxLength={50}
              onChange={(event) => onStoreLoginFieldChange('storeCode', event.target.value)}
              onBlur={() => handleBlur('storeCode')}
            />
          </div>
          <p className="login-field-help">Only needed for staff or store-scoped accounts.</p>
          {touched.storeCode && loginErrors.storeCode && <p className="field-error" role="alert">{loginErrors.storeCode}</p>}
        </div>
      </div>

      <button
        className="btn primary full login-submit"
        onClick={onSuccess}
        disabled={isLoading}
      >
        {isLoading ? 'Logging in...' : <>Log in <ChevronRight size={16} /></>}
      </button>

      {showRegister && (
        <div className="login-toggle">
          Don't have an account?{' '}
          <button type="button" onClick={onScrollToRegister}>
            Register
          </button>
        </div>
      )}

      <div className="login-footer">
        By continuing, you are agreeing to Zopeo's{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
        and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </div>
    </div>
  );
};

export default LoginForm;
